import React, { useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CURRENCY_TITLE } from "@app/constants/currencies";
import { Currency } from "@app/types/currencies";
import { formatToShortDateTime } from "@app/formatters";
import { RatesInfo } from "../../types";
import { TextSize } from "./enums";

type Props = {
  baseCurrency: Currency;
  convertCurrency: Currency;
  ratesInfo: Record<Currency, RatesInfo>;
  textSize?: TextSize;
};

export const CurrencyInfo: React.FC<Props> = ({
  baseCurrency,
  convertCurrency,
  ratesInfo,
  textSize,
}) => {
  const { t } = useTranslation("currency-converter");
  const exchangeRate = useMemo(
    () => ratesInfo[baseCurrency].rates[convertCurrency],
    [ratesInfo, baseCurrency, convertCurrency],
  );

  if (ratesInfo[baseCurrency].isError) {
    return (
      <div className="text-center">
        <div>Ooops, something gone wrong.</div>
        <div>Please reload the page.</div>
      </div>
    );
  }

  if (!ratesInfo[baseCurrency].timestamp) {
    return (
      <div className="text-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="text-center">
      <div
        className={classNames("text-medium text-primary-content", {
          "lg:text-large": textSize === TextSize.LARGE,
        })}
      >
        1.00000 {CURRENCY_TITLE[baseCurrency]} ={" "}
        <span className="text-green-light">{exchangeRate.toFixed(5)}</span>{" "}
        {CURRENCY_TITLE[convertCurrency]}
      </div>
      <div className="text-regular text-base-400">
        {t("rate")}{" "}
        {formatToShortDateTime(ratesInfo[baseCurrency].timestamp * 1000)}
      </div>
    </div>
  );
};
